/**
 * The injectable GraphQL seam for the lexicon service (VE-24948 / U5).
 *
 * Every lexicon service function takes a `GraphQLExecutor` as its first
 * argument rather than reaching for the store itself. That is the difference
 * between code a unit test can exercise and code that needs a running
 * environment — and it is what makes the security-relevant behaviour
 * assertable: NFR-S4's fail-closed rule is proved by injecting an executor
 * whose audit emission fails and checking the lexicon is byte-identical.
 *
 * The default implementation goes through `callGraphQLApi` with NOOP action
 * types and `throwOnError`, which is the established pattern in this repo for
 * "just make the call and throw" (see `documents/services.ts`'s audit-event
 * pager). Lexicon reads and writes are driven by sagas that own their own
 * loading and error state, so dispatching a second layer of request/success/
 * failure actions from the transport would duplicate it.
 */

import callGraphQLApi, {
  GQLApiDispatch,
  GQLApiGetState,
} from '@helpers/callGraphQLApi';

export interface GraphQLRequest {
  readonly query: string;
  readonly variables: Record<string, unknown>;
  readonly operationName?: string;
}

export type GraphQLExecutor = <T>(request: GraphQLRequest) => Promise<T>;

/**
 * Builds an executor bound to the store.
 *
 * `dispatch` is stubbed out because `callGraphQLApi` dispatches
 * unconditionally; the NOOP action types make those dispatches
 * side-effect-free. Same technique as `uploadDocumentAuditLog`.
 */
export function createGraphQLExecutor(
  getState: GQLApiGetState
): GraphQLExecutor {
  const noopDispatch = (() => {}) as GQLApiDispatch;
  return <T>({ query, variables, operationName }: GraphQLRequest): Promise<T> =>
    callGraphQLApi<T>({
      actionTypes: ['NOOP', 'NOOP', 'NOOP'] as never,
      query,
      variables,
      operationName,
      dispatch: noopDispatch as GQLApiDispatch<T>,
      getState,
      throwOnError: true,
    });
}

/**
 * True when a thrown GraphQL error looks like a schema-validation rejection.
 *
 * Used to decide whether to clear the resolved-schema cache and retry once: a
 * validation failure is the symptom of holding a stale schema id after a
 * republish, which is exactly the VE-24546 failure mode. Matching on message
 * text is unpleasant but it is what the existing `redactionStatuses` retry does,
 * and the alternative — treating every error as a possible stale schema — would
 * retry genuine rejections too.
 */
export function isSchemaValidationError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  return message.toLowerCase().includes('validation error');
}
